import {
    GenericImage,
    ReviewModal_Ticket_Fragment,
    ReviewModal_Booking_Fragment,
    LeaveActivityReviewsFragment,
    RentalUnitReviewFragment,
    Maybe,
} from '../generated/graphql';
import TextButton from '@oberoninternal/travelbase-ds/components/action/TextButton';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Box, Flex } from '@rebass/grid';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Locale } from '../entities/Locale';
import createPlaceholder from '../utils/createPlaceholder';
import UnitThumbnail from './designsystem/UnitThumbnail';
import Divider from './Divider';
import Loading from './Loading';
import SummaryBar from './SummaryBar';
import SummaryBarItem, { SummaryBarItemBody, SummaryBarItemField, SummaryBarItemThumbnail } from './SummaryBarItem';
import { useTenantContext } from '../context/TenantContext';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import TitleWrapper from './designsystem/TitleWrapper';

interface Props {
    item: ReviewModal_Booking_Fragment | ReviewModal_Ticket_Fragment | undefined;
    object: LeaveActivityReviewsFragment | RentalUnitReviewFragment | null | undefined;
    webpThumbnail: Maybe<GenericImage | undefined>;
    jpegThumbnail: Maybe<GenericImage | undefined>;
    locale: string | undefined;
    isAnonymous?: boolean;
    loading?: boolean;
    pushUrl: string;
    strapline: string | undefined;
    subtitle: string | undefined;
}

const LeaveReview: FC<React.PropsWithChildren<Props>> = ({
    item,
    object,
    webpThumbnail,
    jpegThumbnail,
    locale,
    isAnonymous,
    loading,
    pushUrl,
    strapline,
    subtitle,
}) => {
    const { brandConfig } = useTenantContext();
    const { push } = useRouter();

    return (
        <Flex flexDirection={['column', null, 'row']} justifyContent="space-between">
            <Box order={1} mr={[null, null, 5, 8]}>
                <StyledTitleWrapper title={<FormattedMessage defaultMessage="Je beoordeling" />} />
                <Description>
                    <FormattedMessage defaultMessage="Wat fijn dat je de tijd neemt om een beoordeling te geven van je accommodatie. Je kunt hiervoor onderstaand formulier gebruiken." />
                    <br />
                    <br />
                    <FormattedMessage defaultMessage="Je beoordeling wordt op de website onder de door jouw beoordeelde accommodatie getoond." />
                    <br />
                    <br />
                    <FormattedMessage defaultMessage="We vermelden daarbij alleen je woonplaats en de maand waarin je te gast was." />
                    <br />
                    <br />
                    <FormattedMessage defaultMessage="De eigenaar van de accommodatie wordt geïnformeerd dat je een beoordeling hebt gegeven en mag een reactie geven op je beoordeling. Ook deze eventuele reactie tonen we op onze website." />
                </Description>

                <Box my={[5, 7]}>
                    <Title elementType="h3" variant="small">
                        <FormattedMessage defaultMessage="Heb je een klacht?" />
                    </Title>
                    <Description>
                        <FormattedMessage
                            defaultMessage="Hopelijk was alles naar wens in de accommodatie. Toch horen we het ook graag als dat niet zo is. Vul dan {complaintForm} in. Wij geven je klacht direct door aan de verhuurder. Uiterlijk binnen twee weken ontvangt je een reactie."
                            values={{
                                complaintForm: (
                                    <TextButton
                                        style={{ display: 'inline-flex' }}
                                        as="a"
                                        href={brandConfig.complaintFormUrl[locale as Locale]}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                    >
                                        <FormattedMessage defaultMessage="het klachtenformulier" />
                                    </TextButton>
                                ),
                            }}
                        />
                    </Description>
                </Box>

                <Divider className="lt-s" mb={6} />
            </Box>
            {isAnonymous ? (
                <Box order={1} mb={6} ml={[3, null, 4]} width={[1, null, '60%']}>
                    {loading && (
                        <Box>
                            <UnitThumbnail loading />
                            <Loading mt={4} spacing={3} />
                        </Box>
                    )}
                    {object?.__typename === 'RentalUnit' && (
                        <SummaryBarItem
                            thumbnail={
                                <SummaryBarItemThumbnail
                                    isClickable
                                    onClick={() => {
                                        push(pushUrl);
                                    }}
                                    title={object.name}
                                    thumbnail={{
                                        sources: [
                                            {
                                                srcSet: webpThumbnail?.transform?.srcSet,
                                                type: 'image/webp',
                                            },
                                            {
                                                srcSet: jpegThumbnail?.transform?.srcSet,
                                                type: 'image/jpeg',
                                            },
                                        ],
                                        fallback: {
                                            src: jpegThumbnail?.transform?.src,
                                        },
                                        placeholder: createPlaceholder(webpThumbnail?.transform?.placeholder),
                                    }}
                                    strapline={strapline}
                                    subTitle={
                                        <SummaryBarItemBody style={{ textTransform: 'capitalize' }} title={subtitle}>
                                            {subtitle}
                                        </SummaryBarItemBody>
                                    }
                                />
                            }
                        >
                            <SummaryBarItemField title={<FormattedMessage defaultMessage="# Accommodatie" />}>
                                <Body variant="small">{object.code}</Body>
                            </SummaryBarItemField>
                        </SummaryBarItem>
                    )}
                </Box>
            ) : (
                <Box order={1} mb={6} ml={[3, null, 4]} width={[1, null, '60%']}>
                    <SummaryBar data={item} showOnMobile hidePrices />
                </Box>
            )}
        </Flex>
    );
};

export default LeaveReview;

const StyledTitleWrapper = styled(TitleWrapper)`
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        text-align: unset;
    }
`;

const Description = styled(Body)`
    margin-top: 1.9rem;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        margin-top: 2.7rem;
    }
`;
